import React, { useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';

import { RollbarContext } from '@rollbar/react';
import useCurrentRoute from 'hooks/useCurrentRoute';
import isPreviewHost from 'utils/isPreviewHost';

import AuthRoute from './authRoute';
import RoutesMapping from './routes';

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_CLIENT_KEY || '';
const NODE_ENV = import.meta.env.NODE_ENV || 'development';
const RECAPTCHA_COOKIEFIRST_CATEGORY = 'necessary';

const AppRoutes = () => {
  const { title, path } = useCurrentRoute();
  const company = localStorage.getItem('company') || 'Lidz';
  useEffect(() => {
    document.title = title ? `${company} - ${title}` : `${company}`;
  }, [title, company]);

  const routesWithRecaptcha = ['/sign-in', '/forgot-password'];
  const loadScriptByURL = (id: string, url: string) => {
    const isScriptExist = document.getElementById(id);

    if (!isScriptExist) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      // reCAPTCHA protects authentication flows and stays classified in CookieFirst as a necessary technical script.
      script.setAttribute('data-cookiefirst-category', RECAPTCHA_COOKIEFIRST_CATEGORY);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      script.src = url;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      script.id = id;
      document.body.appendChild(script);
    }
  };

  const removeScript = (id: string) => {
    const script = document.getElementById(id);
    if (script) {
      document.body.removeChild(script);
    }
  };

  useEffect(() => {
    if (NODE_ENV === 'test') {
      return;
    }
    // Previews skip reCAPTCHA entirely, so loading the script there would only log a domain error
    // and leave a dangling badge.
    if (routesWithRecaptcha.includes(path) && !isPreviewHost()) {
      loadScriptByURL('recaptcha-key', `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`);
    } else {
      removeScript('recaptcha-key');
      const recaptchaElements = document.getElementsByClassName('grecaptcha-badge');

      if (recaptchaElements.length) {
        recaptchaElements[0].remove();
      }
    }
  }, [path]);
  return (
    <React.Suspense fallback={null}>
      <Routes>
        {Object.values(RoutesMapping).map((route) => {
          return (
            <Route
              key={route.title}
              path={route.path}
              element={
                <RollbarContext context={route.path}>
                  <AuthRoute route={route} />
                </RollbarContext>
              }
            />
          );
        })}
      </Routes>
    </React.Suspense>
  );
};

export default AppRoutes;
